import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import ChangePasswordForm from '../../auth/ChangePasswordForm';
import { fetchCreditBalance, fetchCreditTransactions, redeemCreditVoucher } from './creditService';

function relatedLabel(transaction) {
  const parts = [];
  if (transaction.voucher) parts.push(`Voucher ${transaction.voucher.hint}`);
  if (transaction.project) parts.push(`Project ${transaction.project.code || transaction.project.name}`);
  if (transaction.samples?.length) parts.push(transaction.samples.map((sample) => sample.name).join(', '));
  if (transaction.execution_request) parts.push(`Request ${transaction.execution_request.slice(0, 8)}`);
  return parts.join(' · ') || '—';
}

export default function CreditProfilePage() {
  const { user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState({ results: [], next: null, previous: null, count: 0 });
  const [page, setPage] = useState(1);
  const [organizationId, setOrganizationId] = useState(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [balanceData, transactionData] = await Promise.all([
        fetchCreditBalance(organizationId), fetchCreditTransactions(page, organizationId),
      ]);
      setBalance(balanceData);
      if (!organizationId) setOrganizationId(balanceData.organization?.id || null);
      setHistory(transactionData);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to load credit information.');
    } finally {
      setLoading(false);
    }
  }, [organizationId, page]);

  useEffect(() => { load(); }, [load]);

  const redeem = async (event) => {
    event.preventDefault();
    if (!code.trim()) return;
    setRedeeming(true);
    setError('');
    try {
      await redeemCreditVoucher(code.trim(), balance?.organization?.id);
      setCode('');
      setPage(1);
      await load();
    } catch (err) {
      const data = err?.response?.data;
      setError(data?.code?.[0] || data?.detail || 'Voucher redemption failed.');
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <main className="mx-auto max-w-4xl space-y-6" aria-labelledby="profile-title">
      <div>
        <h1 id="profile-title" className="text-2xl font-semibold text-slate-900">{user?.username}'s profile</h1>
        <p className="text-sm text-slate-500">Manage your organization credits.</p>
      </div>
      <ChangePasswordForm />
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" aria-busy={loading}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div><p className="text-sm text-slate-500">Available balance</p><p className="text-4xl font-bold text-slate-900">{balance?.balance ?? '—'} <span className="text-lg font-medium">credits</span></p>{balance?.organizations?.length > 1 ? <label className="mt-2 block text-xs text-slate-500">Organization<select aria-label="Credit organization" value={organizationId || ''} onChange={(e) => { setOrganizationId(e.target.value); setPage(1); }} className="ml-2 rounded border px-2 py-1">{balance.organizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}</select></label> : <p className="text-xs text-slate-500">{balance?.organization?.name}</p>}</div>
          <form onSubmit={redeem} className="flex flex-wrap items-end gap-2">
            <label className="text-sm font-medium text-slate-700">Voucher code<input aria-label="Voucher code" value={code} onChange={(e) => setCode(e.target.value)} className="mt-1 block rounded border border-slate-300 px-3 py-2" autoComplete="off" /></label>
            <button disabled={redeeming || !code.trim()} className="rounded bg-slate-900 px-4 py-2 font-semibold text-white disabled:opacity-50">{redeeming ? 'Redeeming…' : 'Redeem'}</button>
          </form>
        </div>
        {error && <p role="alert" className="mt-3 rounded bg-red-50 p-2 text-sm text-red-700">{error}</p>}
      </section>
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold">Transaction history</h2>
        {loading ? <p className="mt-4 text-sm text-slate-500">Loading transactions…</p> : !history.results?.length ? <p className="mt-4 text-sm text-slate-500">No credit transactions yet.</p> : (
          <div className="mt-3 overflow-x-auto"><table className="min-w-full text-sm"><thead><tr className="border-b text-left text-slate-500"><th className="py-2">Date</th><th>Type</th><th>Description</th><th>Related</th><th className="text-right">Change</th><th className="text-right">Balance</th></tr></thead><tbody>{history.results.map((tx) => <tr key={tx.id} className="border-b border-slate-100"><td className="py-3">{new Date(tx.created_at).toLocaleString()}</td><td>{tx.transaction_type.replaceAll('_', ' ')}</td><td>{tx.description}</td><td className="text-xs text-slate-500">{relatedLabel(tx)}</td><td className={`text-right font-semibold ${tx.amount > 0 ? 'text-emerald-700' : 'text-red-700'}`}>{tx.amount > 0 ? '+' : ''}{tx.amount}</td><td className="text-right">{tx.resulting_balance}</td></tr>)}</tbody></table></div>
        )}
        <div className="mt-4 flex justify-end gap-2"><button disabled={!history.previous} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded border px-3 py-1 disabled:opacity-40">Previous</button><button disabled={!history.next} onClick={() => setPage((p) => p + 1)} className="rounded border px-3 py-1 disabled:opacity-40">Next</button></div>
      </section>
    </main>
  );
}
